import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  RESUMO_FINANCEIRO_TORNEIO,
  SERIE_POR_DIA_TORNEIO,
  QUEBRA_POR_CATEGORIA_TORNEIO,
  ResumoFinanceiroTorneioData,
  SeriePorDiaTorneioData,
  QuebraPorCategoriaTorneioData,
  FiltroTorneioVars,
} from '../services/financeiroService';
import type { ResumoFinanceiro, PontoSerieFinanceira, QuebraFinanceira, PeriodoFinanceiro } from '../types/financeiro';

// Espelha hooks/useFinanceiroLiga.ts (financeiro-dashboard-liga-torneio, Phase 3), trocando
// `ligaId` por `torneioId` — as 3 queries de torneio exigem `torneioId: ID!` (Task 2/Task 4 do
// plano desta fase). Mesmas regras do molde de liga:
// 1. `fetchPolicy: 'no-cache'` nas 3 — a sub-aba nunca lê de cache stale entre trocas de período.
// 2. `resumoIndisponivel` distingue "servidor devolveu null" (contrato nullable) de "falha de
//    rede" (`error`).
// 3. `refetch` dispara as 3 novamente — usado pela ação "Tentar novamente".
// 4. Nenhuma soma de valor monetário aqui: o hook só repassa o que veio das 3 queries.
// 5. `serieError`/`serieLoading`/`refetchSerie` expõem o estado da 3ª query ISOLADO do gate
//    geral — o consumidor decide como apresentar uma falha isolada da série sem confundi-la
//    com "período sem lançamentos" (code review da Phase 5, attempt 2).

export interface UseFinanceiroTorneioResult {
  periodo: PeriodoFinanceiro;
  setPeriodo: (periodo: PeriodoFinanceiro) => void;
  resumo: ResumoFinanceiro | null;
  serie: PontoSerieFinanceira[];
  quebra: QuebraFinanceira[];
  loading: boolean;
  error: Error | undefined;
  resumoIndisponivel: boolean;
  serieLoading: boolean;
  serieError: Error | undefined;
  refetchSerie: () => void;
  refetch: () => void;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateLocal(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Primeiro e último dia do mês corrente, por aritmética de Date LOCAL (nunca UTC) — usar UTC
// deslocaria o período para quem está em UTC-3.
function periodoMesCorrente(): PeriodoFinanceiro {
  const agora = new Date();
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), 1);
  const fim = new Date(agora.getFullYear(), agora.getMonth() + 1, 0);
  return { inicio: formatDateLocal(inicio), fim: formatDateLocal(fim) };
}

export function useFinanceiroTorneio(torneioId: string): UseFinanceiroTorneioResult {
  const [periodo, setPeriodo] = useState<PeriodoFinanceiro>(periodoMesCorrente());

  const resumoQuery = useQuery<ResumoFinanceiroTorneioData, FiltroTorneioVars>(RESUMO_FINANCEIRO_TORNEIO, {
    variables: { torneioId, filtro: periodo },
    skip: !torneioId,
    fetchPolicy: 'no-cache',
  });

  const serieQuery = useQuery<SeriePorDiaTorneioData, FiltroTorneioVars>(SERIE_POR_DIA_TORNEIO, {
    variables: { torneioId, filtro: periodo },
    skip: !torneioId,
    fetchPolicy: 'no-cache',
  });

  const quebraQuery = useQuery<QuebraPorCategoriaTorneioData, FiltroTorneioVars>(QUEBRA_POR_CATEGORIA_TORNEIO, {
    variables: { torneioId, filtro: periodo },
    skip: !torneioId,
    fetchPolicy: 'no-cache',
  });

  const resumo = resumoQuery.data?.resumoFinanceiroTorneio ?? null;
  // Série é decorativa (gráfico) -- seu erro/loading NÃO entra no gate geral da sub-aba, para que
  // os cards de resumo e a quebra por categoria continuem utilizáveis mesmo que a 3ª chamada
  // (série) falhe isoladamente.
  const serie = serieQuery.data?.seriePorDiaTorneio ?? [];
  const quebra = quebraQuery.data?.quebraPorCategoriaTorneio ?? [];
  const loading = resumoQuery.loading || quebraQuery.loading;
  const error = resumoQuery.error || quebraQuery.error;
  const resumoIndisponivel = !resumoQuery.loading && !resumoQuery.error && resumo === null;

  const refetchSerie = () => {
    serieQuery.refetch();
  };

  const refetch = () => {
    resumoQuery.refetch();
    serieQuery.refetch();
    quebraQuery.refetch();
  };

  return {
    periodo,
    setPeriodo,
    resumo,
    serie,
    quebra,
    loading,
    error,
    resumoIndisponivel,
    serieLoading: serieQuery.loading,
    serieError: serieQuery.error,
    refetchSerie,
    refetch,
  };
}
